import { IncomingMessage } from 'http';

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { config } from '@utils/config';

import { getClientIp, LoginRedirectParams } from '@modules/base/auth';

import { Enum } from './enum';

/**
 * IMPORTANT: If used on server, make sure to handle 401 properly if request call is catched! (It should redirect user to the login page)
 * Also bind request to the SSR context, if the source is under auth.
 */
export const apiClient: AxiosInstance & {
  request: <T = any, R = AxiosResponse<T, any>, D = any>(
    requestConfig: AxiosRequestConfig<D>,
    req?: IncomingMessage,
  ) => Promise<R>;
} = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

type LogoutHandler = ((params: LoginRedirectParams) => void) | undefined;
let handleLogout: LogoutHandler;

export const setLogoutHandler = (handler?: LogoutHandler) => {
  handleLogout = handler;
};

const originalRequest = apiClient.request;

apiClient.request = async <T = any, R = AxiosResponse<T, any>, D = any>(
  requestConfig: AxiosRequestConfig<D>,
  req?: IncomingMessage,
) => {
  if (req && typeof window === 'undefined') {
    // if request is on the server,
    // get requester ip and set it into the header
    const ip = getClientIp(req);
    if (ip)
      requestConfig.headers = {
        'x-forwarded-for': ip,
        ...requestConfig.headers,
      };
  }

  const result = await originalRequest<T, R, D>(requestConfig).catch((e) => {
    if (handleLogout && axios.isAxiosError(e)) {
      if (isTokenInvalidError(e))
        handleLogout({
          withLogoutRequest: isTokenInvalidError(e, [
            'Refresh token is about to expire',
            'Session does not recognize this ip address',
            'Session was not found',
          ]),
          reason: 'login_required',
        });
    }
    throw e;
  });

  return result;
};

const TokenErrors = Enum(
  'Refresh token has expired',
  'Refresh token is about to expire',
  'Token has expired at',
  'Session was not found',
  'Session does not recognize this ip address',
  'Session has expired at',
  'Missing authorization or authentication',
);

/**
 * Checks if axios error is related to token
 *
 *  @param e - AxiosError
 *  @param expectedError - Array of expected error messages
 */
export const isTokenInvalidError = (
  e: AxiosError,
  expectedError = Object.values(TokenErrors),
) => {
  return (
    e.isAxiosError &&
    e.response?.status === 401 &&
    typeof e.response.data === 'string' &&
    expectedError.some((error) => (e.response?.data as string).includes(error))
  );
};
